import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Boxes, Package, ShieldCheck } from 'lucide-react';
import { moduleSuitsFacilityType, MODULE_CATALOGUE, type ModuleCode } from '@his/shared';
import { get, put } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { cn } from '@/lib/utils';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { RecordState } from '@/components/ui/record-state';
import { Alert, Badge, Checkbox, Field, Input } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { isValidTotp, TotpField } from './OwnerConfirm';
import { FACILITY_LABELS, type Plan } from './PlansPage';
import { safeReturnTo } from '../opd/BookAppointmentPage';

export function PlanFormPage() {
  const { planId = '' } = useParams();
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get('returnTo'), '/owner/plans');

  const plans = useQuery({
    queryKey: ['plans', 'all'],
    queryFn: () => get<Plan[]>('/platform/plans'),
  });
  const plan = plans.data?.find((row) => row.id === planId) ?? null;

  if (!plan) {
    return (
      <FormScreen title="Edit plan" icon={Package} backTo={returnTo}>
        <RecordState error={plans.error} missing={Boolean(plans.data)} />
      </FormScreen>
    );
  }

  return <PlanForm key={plan.id} plan={plan} returnTo={returnTo} />;
}

function toForm(plan: Plan) {
  return {
    name: plan.name,
    description: plan.description ?? '',
    trialDays: String(plan.trialDays),
    modules: plan.moduleFlags as ModuleCode[],
  };
}

function PlanForm({ plan, returnTo }: { plan: Plan; returnTo: string }) {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [initial] = useState(() => toForm(plan));
  const [form, setForm] = useState(initial);
  const [reason, setReason] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  const offered = MODULE_CATALOGUE.filter((module) => moduleSuitsFacilityType(module.code, plan.facilityType));
  const hidden = MODULE_CATALOGUE.length - offered.length;

  const toggleModule = (code: ModuleCode) =>
    setForm((current) => ({
      ...current,
      modules: current.modules.includes(code) ? current.modules.filter((m) => m !== code) : [...current.modules, code],
    }));

  const save = useMutation({
    mutationFn: () =>
      put(`/platform/plans/${plan.id}`, {
        totpCode,
        reason: reason.trim(),
        code: plan.code,
        name: form.name,
        description: form.description || undefined,
        trialDays: Number(form.trialDays),
        moduleFlags: form.modules,
        facilityType: plan.facilityType,
        isActive: true,
      }),
    onSuccess: () => {
      toast.success('Plan updated');
      void queryClient.invalidateQueries({ queryKey: ['plans'] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const ready = Boolean(form.name) && reason.trim().length >= 5 && isValidTotp(totpCode);
  const dirty = !save.isSuccess && (JSON.stringify(form) !== JSON.stringify(initial) || reason !== '' || totpCode !== '');

  return (
    <FormScreen
      title={`Edit the ${FACILITY_LABELS[plan.facilityType].toLowerCase()} plan`}
      description={`${plan.tenantCount} facility(ies) run on this bundle. Per-facility overrides always win.`}
      icon={Package}
      backTo={returnTo}
      dirty={dirty}
      wide
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready) return;
        setError(null);
        save.mutate();
      }}
    >
      <FormSection title="Plan" icon={Package} columns={3}>
        <Field label="Name" required className="sm:col-span-2">
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <Field label="Trial days">
          <Input type="number" min={0} max={365} value={form.trialDays} onChange={(e) => setForm({ ...form, trialDays: e.target.value })} />
        </Field>
        <Field label="Description" className="sm:col-span-2 lg:col-span-3">
          <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </Field>
      </FormSection>

      <FormSection title="Modules included" icon={Boxes} columns={1}>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="secondary">{offered.length} available</Badge>
          {hidden > 0 && (
            <p className="text-xs text-muted-foreground">
              {hidden} module{hidden === 1 ? '' : 's'} that only a hospital runs are not offered here.
            </p>
          )}
        </div>
        <div className="grid gap-1.5 sm:grid-cols-3">
          {offered.map((module) => (
            <label
              key={module.code}
              className={cn(
                'flex items-start gap-2 rounded-md border p-2 text-sm',
                form.modules.includes(module.code) && 'border-primary/40 bg-primary/5',
                module.code === 'core' ? 'opacity-60' : 'cursor-pointer',
              )}
            >
              <Checkbox
                className="mt-0.5"
                disabled={module.code === 'core'}
                checked={form.modules.includes(module.code)}
                onCheckedChange={() => toggleModule(module.code)}
              />
              <span>
                <span className="font-medium">{module.name}</span>
                <span className="block text-xs text-muted-foreground">{module.description}</span>
              </span>
            </label>
          ))}
        </div>
      </FormSection>

      <FormSection title="Confirm" icon={ShieldCheck} columns={1}>
        <Alert>
          This changes the licence of every facility that holds this package ({plan.tenantCount}). Facilities on their own server pick it up at their next
          check-in.
        </Alert>
        <Field label="Reason" required hint="Recorded in the platform audit trail — a ticket number is ideal.">
          <Input value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
        <TotpField value={totpCode} onChange={setTotpCode} />
        {error && <Alert variant="destructive">{error}</Alert>}
      </FormSection>

      <FormActions submitLabel="Save plan" loading={save.isPending} incomplete={!ready} cancelTo={returnTo} bleed={false} />
    </FormScreen>
  );
}
